// Action-request execution (T1.3 / REQ-62 / D-5 REVERSED)
//
// The agent NEVER writes — it proposes a typed action_request; the UI renders
// confirm/cancel and, on confirm, performs the write here via the trips REST
// API with the user's own JWT. Shared by every chat surface so proposals
// behave identically everywhere.

import {
  createActivity,
  createTrip,
  deleteActivity,
  getActivities,
  getAllTrips,
  updateActivity,
  updateTrip,
  addChecklistItems,
} from './api/tripsApi';
import { extractTripUuid } from './utils/idUtils';

export type ActionRequestAction =
  | 'CREATE_TRIP'
  | 'UPDATE_TRIP'
  | 'ADD_ACTIVITIES'
  | 'UPDATE_ACTIVITY'
  | 'DELETE_ACTIVITY'
  // Packing / to-bring list. Must stay in step with agents/schema.py — the
  // agent can only propose what this union can execute.
  | 'ADD_CHECKLIST_ITEMS';

export interface ActionRequest {
  action: ActionRequestAction;
  trip_id?: string;
  payload: Record<string, any>;
}

export type ActionResolution = 'confirmed' | 'dismissed' | 'failed';

export interface ActionResult {
  note: string;
  tripId?: string;
}

// Chat-surface context threaded into the write. `conversationId` links the
// created trip back to the chat that planned it (shown on the trips page and
// re-opened when the trip's chat is resumed).
export interface ActionContext {
  conversationId?: string;
  // The trip the UI KNOWS this chat is about (the open trip, or the one this
  // conversation created). For every trip-scoped write (add/update/delete) this
  // is the authoritative trip id — preferred over the agent's echoed
  // request.trip_id, which can be a composite key or an outright wrong value
  // (e.g. the destination name), producing a 404 on the activity call (D-5:
  // the UI executes with real data, never the model's transcription).
  existingTripId?: string;
  // Safety net: when the agent's CREATE_TRIP payload carries no `activities`
  // but it recommended specific places in the SAME message (the chips shown
  // under the card), save those as (untimed) stops so the trip isn't created
  // with only district names. Redundant once the agent embeds activities in the
  // payload, but harmless — payload activities always take precedence.
  fallbackActivities?: Array<Record<string, any>>;
}

// The trip id to actually call the API with for a trip-scoped action: the UI's
// known trip wins over the agent's echoed value, and either is normalised to the
// bare UUID the routes expect (strips a DynamoDB `TRIP#` prefix).
const resolveTripId = (request: ActionRequest, context: ActionContext): string =>
  extractTripUuid(context.existingTripId || request.trip_id || '');

// Fallback stops for a CREATE_TRIP whose payload carries no `activities`:
// harvest the response's OTHER structured fields, richest first — the top-level
// `activities` array ({name, description}, emitted on PLAN_ACTIVITIES-style
// turns), then the recommended-place chips ({name, place_id}). Deduped by name.
// This is the safety net behind the prompt rule that CONFIRM_TRIP must attach a
// full action_request — a prompt regression still yields stops, not an empty trip.
// Agent/tool modes (Google vocabulary) → the server's trip travelMode.
const toTravelMode = (raw: unknown): 'drive' | 'walk' | 'transit' | undefined => {
  const value = String(raw || '').toLowerCase();
  if (value === 'drive' || value === 'driving') return 'drive';
  if (value === 'walk' || value === 'walking') return 'walk';
  if (value === 'transit') return 'transit';
  return undefined;
};

export const collectFallbackActivities = (source: {
  activities?: Array<Record<string, any>>;
  locations_recommendations?: Array<Record<string, any>>;
}): Array<Record<string, any>> => {
  const activities = Array.isArray(source?.activities) ? source.activities : [];
  const recommendations = Array.isArray(source?.locations_recommendations)
    ? source.locations_recommendations
    : [];
  const seen = new Set<string>();
  return [...activities, ...recommendations].filter((item) => {
    const name = String(item?.name || item?.title || '').trim().toLowerCase();
    if (!name || seen.has(name)) return false;
    seen.add(name);
    return true;
  });
};

// Map an agent activity proposal (calendar-ready shape: {name, description,
// date, start_time, end_time, place_id, location_text}) → the trips API's
// CreateActivity DTO. Shared by CREATE_TRIP and ADD_ACTIVITIES. Start/end times
// become the activity's dateTime/endDateTime so they land on the calendar.
const toActivityCreate = (activity: any) => ({
  title: activity?.name || activity?.title || 'Activity',
  description: activity?.description || undefined,
  location: activity?.location_text || activity?.location || activity?.name || activity?.title || undefined,
  placeId: activity?.place_id || activity?.placeId || undefined,
  dateTime: actionDateToIso(activity?.date, activity?.start_time),
  endDateTime: actionDateToIso(activity?.date, activity?.end_time),
});

export const ACTION_LABELS: Record<ActionRequestAction, string> = {
  CREATE_TRIP: 'Create this trip',
  UPDATE_TRIP: 'Update this trip',
  ADD_ACTIVITIES: 'Add these activities',
  UPDATE_ACTIVITY: 'Update this activity',
  DELETE_ACTIVITY: 'Remove this activity',
  ADD_CHECKLIST_ITEMS: 'Add to the list',
};

// MM-DD-YYYY (+ optional HH:MM) → ISO 8601 for the trips API
export const actionDateToIso = (date?: string, time?: string): string | undefined => {
  if (!date) return undefined;
  const parts = date.split('-');
  if (parts.length !== 3) return undefined;
  const [mm, dd, yyyy] = parts;
  return `${yyyy}-${mm}-${dd}T${time && /^\d{2}:\d{2}$/.test(time) ? time : '09:00'}:00`;
};

// The agent may return destinations as an array, an array of {name}, or a
// comma-separated string. The trips API strictly requires a string[]; coerce.
export const toDestinationArray = (value: any): string[] => {
  if (Array.isArray(value)) {
    return value.map((v) => (typeof v === 'string' ? v : v?.name || v?.title || '')).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

// Build the trips API's typed dates object, or undefined when no dates given.
const toTripDates = (dates: any) => {
  const startDate = actionDateToIso(dates?.startDate)?.slice(0, 10);
  const endDate = actionDateToIso(dates?.endDate)?.slice(0, 10);
  return startDate && endDate ? { type: 'concrete', startDate, endDate } : undefined;
};

// Derive the trip's date span from its activities (start = earliest, end =
// latest). The agent sometimes proposes a trip date that doesn't match the
// activity dates; the activities are the concrete plan, so when they carry dates
// we take those — the trip always covers its stops. Returns undefined when no
// activity has a date (e.g. recommendation fallbacks) → caller uses payload.dates.
const datesFromActivities = (activities: any[]) => {
  const days = (activities || [])
    .map((a) => actionDateToIso(a?.date)?.slice(0, 10))
    .filter((d): d is string => Boolean(d))
    .sort(); // ISO YYYY-MM-DD sorts chronologically
  return days.length ? { type: 'concrete', startDate: days[0], endDate: days[days.length - 1] } : undefined;
};

// The server requires travelers (when present) to be { adults >= 1, children >= 0 }
// as integers. The agent may omit children or send strings — coerce, or omit
// entirely when no travelers were given.
const toTravelers = (value: any): { adults: number; children: number } | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const adults = Number.parseInt(value.adults, 10);
  const children = Number.parseInt(value.children, 10);
  return {
    adults: Number.isInteger(adults) && adults >= 1 ? adults : 1,
    children: Number.isInteger(children) && children >= 0 ? children : 0,
  };
};

// Executes a confirmed proposal. Returns a user-facing note and optional trip id; throws on failure.
/**
 * Re-time stops that ALREADY EXIST, from the activity list on an UPDATE_TRIP.
 *
 * Matched by name, case- and whitespace-insensitively, because the agent echoes
 * the names it showed the user rather than ids (the active_trip snapshot it
 * reasons over carries no ids at all). Each name is consumed on first match, so
 * two stops sharing a name re-time one apiece.
 *
 * A proposal matching NOTHING is skipped on purpose: on an update, an
 * unrecognised name is far more likely a renamed stop than a new one, and
 * creating it would duplicate the itinerary the user is tidying.
 *
 * Kept in step with the mobile copy (simple-mobile-app/src/lib/action-requests).
 */
async function retimeExistingActivities(tripId: string, proposed: unknown): Promise<number> {
  if (!Array.isArray(proposed) || proposed.length === 0) return 0;

  const key = (name: unknown) => String(name || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const existing = await getActivities(tripId).catch(() => [] as any[]);
  // A QUEUE per name, not a single id: an itinerary legitimately repeats names
  // ("Lunch", "Hotel check-in"), and one slot per name meant the second
  // proposal matched nothing while claiming to be consumed.
  const byName = new Map<string, string[]>();
  for (const activity of existing) {
    const k = key(activity.title);
    if (!k) continue;
    const queue = byName.get(k);
    if (queue) queue.push(activity.id);
    else byName.set(k, [activity.id]);
  }

  let moved = 0;
  for (const item of proposed) {
    const record = (item || {}) as Record<string, any>;
    const k = key(record.name || record.title);
    // shift(), so repeated names re-time one stop apiece in the order given.
    const activityId = k ? byName.get(k)?.shift() : undefined;
    if (!activityId) continue;

    const dateTime = actionDateToIso(record.date, record.start_time);
    if (!dateTime) continue;
    // Only when an end was actually GIVEN: actionDateToIso defaults a missing
    // time to 09:00, so passing an absent end_time through would stamp an end
    // BEFORE the start (13:30 → 09:00) and corrupt the stop we are moving.
    const endDateTime = record.end_time ? actionDateToIso(record.date, record.end_time) : undefined;

    try {
      await updateActivity(tripId, activityId, {
        dateTime,
        ...(endDateTime ? { endDateTime } : {}),
      } as any);
      moved += 1;
    } catch {
      // Best effort per stop: the trip fields are already saved, so one failed
      // row must not turn a partial success into an error.
    }
  }
  return moved;
}

export const executeActionRequest = async (
  request: ActionRequest,
  context: ActionContext = {},
): Promise<ActionResult> => {
  const payload = request.payload || {};
  switch (request.action) {
    case 'CREATE_TRIP': {
      // Idempotency: never create a second trip for the same chat. If one
      // already exists for this conversation, return it instead of creating a
      // duplicate (guards against double-confirm, re-proposals, and races).
      if (context.conversationId) {
        try {
          const existing = (await getAllTrips()).find(
            (trip) => trip.conversation_id === context.conversationId,
          );
          if (existing) {
            return {
              note: `Trip "${existing.title || 'trip'}" already exists for this chat`,
              tripId: extractTripUuid(existing.id),
            };
          }
        } catch {
          // If the lookup fails, fall through and create — better a possible
          // duplicate than blocking trip creation entirely.
        }
      }

      const destinations = toDestinationArray(payload.destinations);
      const budget =
        payload.budget && typeof payload.budget === 'object' && payload.budget.amount
          ? { amount: Number(payload.budget.amount), currency: payload.budget.currency || 'USD' }
          : undefined;

      // The itinerary planned in chat rides along on the CREATE_TRIP payload as
      // `activities`, so the trip is created WITH its stops in one confirm. When
      // the payload has no activities, fall back to the message's recommended
      // places. Compute this BEFORE createTrip so the trip's dates can be derived
      // from the activities (keeps trip range consistent with its stops).
      const payloadActivities = Array.isArray(payload.activities) ? payload.activities : [];
      const proposedActivities = payloadActivities.length
        ? payloadActivities
        : (context.fallbackActivities || []);

      const created = await createTrip({
        title: payload.title || destinations.join(', ') || 'New trip',
        destinations,
        dates: datesFromActivities(payloadActivities) || toTripDates(payload.dates),
        status: 'planning',
        budget,
        travelers: toTravelers(payload.travelers),
        description: payload.description || undefined,
        // T1.16: the agent picks the trip's default travel mode at plan time
        // (road trip → drive, city break → walk); server legs recompute with it.
        travelMode: toTravelMode(payload.travel_mode ?? payload.travelMode),
        // Link the trip back to the chat that planned it.
        conversation_id: context.conversationId || undefined,
        /**
         * PRIVATE, always, for an agent-proposed trip.
         *
         * Confirming a suggestion is not a decision to publish. Without this the
         * server inherits the ACCOUNT default (D-35), so an open account had
         * every agent trip land as 'public' — announced to followers on create,
         * from a chat the user was still drafting in. Sharing is a separate,
         * deliberate act: the owner widens it in Trip settings.
         *
         * Manual creation is untouched and still chooses its own visibility.
         */
        visibility: 'private',
      } as any);
      const tripId = extractTripUuid(created.id);

      // The trip already exists — a failed activity must not discard it, so we
      // add them best-effort and report how many landed.
      let added = 0;
      for (const activity of proposedActivities) {
        const dto = toActivityCreate(activity);
        if (!dto.title) continue;
        try {
          await createActivity(tripId, dto);
          added += 1;
        } catch (error) {
          console.error('Failed to add activity during trip creation:', error);
        }
      }

      const title = (created as any)?.title || payload.title || 'trip';
      const note = added
        ? `Trip "${title}" created with ${added} ${added === 1 ? 'stop' : 'stops'}`
        : `Trip "${title}" created`;
      return { note, tripId };
    }
    case 'UPDATE_TRIP': {
      const tripId = resolveTripId(request, context);
      if (!tripId) throw new Error('missing trip_id');
      const { dates, destinations, travelers, activities, ...rest } = payload;
      await updateTrip({
        id: tripId,
        ...rest,
        ...(destinations !== undefined ? { destinations: toDestinationArray(destinations) } : {}),
        ...(dates !== undefined ? { dates: toTripDates(dates) } : {}),
        ...(travelers !== undefined ? { travelers: toTravelers(travelers) } : {}),
      } as any);

      /**
       * An UPDATE_TRIP payload can carry ACTIVITIES. They used to ride along in
       * `...rest` into the trip PATCH, where the server ignores them — so "move
       * Day 1 to the 17th" shifted the trip's DATES and left every stop where
       * it was. Silently: the write succeeded.
       *
       * The agent reaches for this shape because the action schema has no verb
       * for "re-time several existing stops" — ADD_ACTIVITIES would duplicate
       * them, UPDATE_ACTIVITY is one at a time. So treat activities on an
       * UPDATE as a RE-TIMING of stops that already exist, never as new ones.
       */
      const moved = await retimeExistingActivities(tripId, activities);
      return {
        note: moved > 0 ? `Trip updated · ${moved} ${moved === 1 ? 'stop' : 'stops'} moved` : 'Trip updated',
      };
    }
    case 'ADD_ACTIVITIES': {
      const tripId = resolveTripId(request, context);
      if (!tripId) throw new Error('missing trip_id');
      const activities = payload.activities || [];
      if (!activities.length) throw new Error('no activities in proposal');
      for (const activity of activities) {
        await createActivity(tripId, toActivityCreate(activity));
      }
      return { note: `Added ${activities.length} ${activities.length === 1 ? 'activity' : 'activities'}` };
    }
    case 'UPDATE_ACTIVITY': {
      const tripId = resolveTripId(request, context);
      if (!tripId || !payload.activity_id) throw new Error('missing trip_id/activity_id');
      const { activity_id, ...changes } = payload;
      // Full field parity with the manual editor (T1.6): title, description,
      // place, and BOTH start/end times. A time change needs the activity's
      // date too — the agent echoes it (see prompt); without it the time is
      // dropped rather than mis-dated.
      const updates: Record<string, any> = {};
      if (changes.name || changes.title) updates.title = changes.name || changes.title;
      if (changes.description !== undefined) updates.description = changes.description;
      if (changes.location_text || changes.location) updates.location = changes.location_text || changes.location;
      if (changes.place_id || changes.placeId) updates.placeId = changes.place_id || changes.placeId;
      if (changes.date || changes.start_time) updates.dateTime = actionDateToIso(changes.date, changes.start_time);
      if (changes.end_time) updates.endDateTime = actionDateToIso(changes.date, changes.end_time);
      await updateActivity(tripId, activity_id, updates);
      return { note: 'Activity updated' };
    }
    case 'DELETE_ACTIVITY': {
      const tripId = resolveTripId(request, context);
      if (!tripId || !payload.activity_id) throw new Error('missing trip_id/activity_id');
      await deleteActivity(tripId, payload.activity_id);
      return { note: 'Activity removed' };
    }
    case 'ADD_CHECKLIST_ITEMS': {
      const tripId = resolveTripId(request, context);
      if (!tripId) throw new Error('missing trip_id');

      const raw = Array.isArray(payload.items) ? payload.items : [];
      const drafts = raw
        .map((item: any) => ({
          title: String(item?.title || item?.name || '').trim(),
          description: String(item?.description || '').trim() || undefined,
        }))
        .filter((item: { title: string }) => item.title);
      if (drafts.length === 0) throw new Error('no items to add');

      /**
       * ONE request for the whole batch. The 30-item cap is checked server-side
       * against everything being added at once, so a call per item could slip
       * past the limit and leave a half-applied list behind.
       */
      const created = await addChecklistItems(tripId, drafts);
      return {
        note:
          created.length === 1
            ? `Added "${created[0].title}" to the list`
            : `Added ${created.length} items to the list`,
        tripId,
      };
    }
    default:
      throw new Error(`Unknown action ${(request as any).action}`);
  }
};
